/**
 * 중증도분류 수행결과 공통 타입·유틸 (Mass / KTAS / Radiation).
 */

/** 중증도분류 답변항목: red, yellow, green, black, 미분류 — 캡슐 배경/텍스트 */
export const TRIAGE_CAPSULE: Record<string, string> = {
	red: 'bg-red-500 text-white',
	yellow: 'bg-amber-400 text-gray-900',
	green: 'bg-green-500 text-white',
	black: 'bg-gray-900 text-white',
	미분류: 'bg-white-400 border border-red-600 text-red-600',
	// API 영문 코드 매핑
	R: 'bg-red-500 text-white',       // Red (긴급)
	Y: 'bg-amber-400 text-gray-900',  // Yellow (응급)
	G: 'bg-green-500 text-white',     // Green (비응급)
	B: 'bg-gray-900 text-white',      // Black (사망)
	None: 'bg-gray-900 text-white',   // None (사망/미분류)
};

/** SALT 등 기존 red/yellow/green/black 캡슐 (정답율 막대 등) */
export const saltTriageCapsuleStyle = (level: string): string =>
	TRIAGE_CAPSULE[level] ?? 'bg-white border border-gray-500 text-gray-500';

/** API 영문 코드를 한글로 변환 */
export const triageCodeToKorean: Record<string, string> = {
	R: '긴급',
	Y: '응급',
	G: '비응급',
	B: '사망',
	None: '사망',
	red: '긴급',
	yellow: '응급',
	green: '비응급',
	black: '사망',
	미분류: '미분류',
};

/** Triage 코드를 한글로 변환 (화면 표시용) */
export function formatTriageLevel(level: string): string {
	const trimmed = String(level ?? '').trim();
	return (triageCodeToKorean[trimmed] ?? trimmed) || '미분류';
}

export function isKtasContentCode(code: string | null | undefined): boolean {
    return String(code ?? '').trim().toLowerCase() === 'ktas';
}

/**
 * KTAS 표시용 색 이름 → 단계 (blue=1 … white=5, black=미분류).
 * API·목데이터가 숫자 대신 색 문자열을 줄 때 사용.
 */
const COLOR_WORD_TO_KTAS_LEVEL: Record<string, 1 | 2 | 3 | 4 | 5 | '미분류'> = {
    blue: 1,
    red: 2,
    yellow: 3,
    green: 4,
    white: 5,
    black: '미분류',
};

/**
 * KTAS Level 1~5 · `미분류` 문자열만 파싱 (`Level 1`, `1`, `L3` 등).
 * 세션 KTAS 추론용 — SALT 색 이름은 제외.
 */
export function parseKtasLevelKey(raw: string): 1 | 2 | 3 | 4 | 5 | '미분류' | null {
    const s = String(raw).trim().toLowerCase();
    if (!s || s === '—') return null;
    if (s === '미분류') return '미분류';
    const m = s.match(/(?:level|ktas|l)\s*[:]?\s*([1-5])\b/i) ?? s.match(/^([1-5])$/);
    if (m) return Number(m[1]) as 1 | 2 | 3 | 4 | 5;
    return null;
}

/**
 * KTAS 카드·캡슐 표시: 숫자/레벨 파싱 후, 없으면 SALT 색 문자열을 단계로 매핑.
 */
export function resolveKtasDisplayLevelKey(raw: string): 1 | 2 | 3 | 4 | 5 | '미분류' | null {
    const strict = parseKtasLevelKey(raw);
    if (strict != null) return strict;
    const s = String(raw).trim().toLowerCase();
    const fromColorWord = COLOR_WORD_TO_KTAS_LEVEL[s];
    if (fromColorWord !== undefined) {
        return fromColorWord === '미분류' ? '미분류' : fromColorWord;
    }
    return null;
}

/** 전체 참가자 뷰: KTAS 단계별 막대 순서 (색: blue→1 … white→5, black→미분류) */
export const KTAS_RATE_BAR_ORDER = [1, 2, 3, 4, 5, '미분류'] as const;

export type KtasRateBarLevelKey = (typeof KTAS_RATE_BAR_ORDER)[number];

/** `correctRateByColor`: SALT 4색 + 선택적 blue/white(단계 1·5 비율) */
export function ktasRateFromSaltByColor(
    byColor: { red: number; yellow: number; green: number; black: number; blue?: number; white?: number },
    level: KtasRateBarLevelKey,
): number {
    switch (level) {
        case 1:
            return Math.min(100, Math.max(0, byColor.blue ?? 0));
        case 2:
            return Math.min(100, Math.max(0, byColor.red ?? 0));
        case 3:
            return Math.min(100, Math.max(0, byColor.yellow ?? 0));
        case 4:
            return Math.min(100, Math.max(0, byColor.green ?? 0));
        case 5:
            return Math.min(100, Math.max(0, byColor.white ?? 0));
        case '미분류':
            return Math.min(100, Math.max(0, byColor.black ?? 0));
        default:
            return 0;
    }
}

export function ktasLevelCapsuleStyle(level: string): string {
    const key = resolveKtasDisplayLevelKey(level);
    if (key === '미분류') return 'bg-white border border-red-600 text-red-600';
    if (key === null) return 'bg-white border border-gray-500 text-gray-500';
    /** blue:L1 · red:L2 · yellow:L3 · green:L4 · white:L5 */
    const byLevel: Record<1 | 2 | 3 | 4 | 5, string> = {
        1: 'bg-blue-600 text-white',
        2: 'bg-red-500 text-white',
        3: 'bg-yellow-400 text-gray-900',
        4: 'bg-green-500 text-white',
        5: 'bg-white border border-gray-300 text-gray-900',
    };
    return byLevel[key];
}

export function displayCell(v: string | number | null | undefined): string {
    if (v == null) return '—';
    const s = String(v).trim();
    return s || '—';
}

/** KTAS: 원본 값 → `Level n` / `미분류` 표기 */
export function formatKtasLevelDisplay(
    raw: string,
    t: (key: string, options?: { n?: number }) => string,
): string {
    const key = resolveKtasDisplayLevelKey(raw);
    if (key === '미분류') return t('simulationResults.ktasUnclassified');
    if (key === null) return displayCell(raw);
    return t('simulationResults.ktasLevelDisplay', { n: key });
}

/** 카드·분류 캡슐: KTAS면 Level 색, 아니면 SALT 팔레트 (`forceKtas`: contentCode 없을 때 행으로 추론) */
export function resolveClassificationCapsuleStyle(
    level: string,
    contentCode: string | null | undefined,
    forceKtas?: boolean,
): string {
    if (forceKtas || isKtasContentCode(contentCode)) return ktasLevelCapsuleStyle(level);
    return saltTriageCapsuleStyle(level);
}

/** 외상·복합 2단 분류(캡슐 2열) — `radiation` 전용 */
export function isRadiationDualStageContentCode(code: string | null | undefined): boolean {
    return String(code ?? '').trim().toLowerCase() === 'radiation';
}

/** `content_code`가 없어도 `correctKtasLevel`·Level 1~5 형태 값이 있으면 KTAS UI */
export function inferKtasFromRow(row: TriagePatientRow, firstCorrect: string, firstUser: string): boolean {
    if (row.correctKtasLevel != null && String(row.correctKtasLevel).trim() !== '') return true;
    if (parseKtasLevelKey(String(firstCorrect)) != null) return true;
    if (parseKtasLevelKey(String(firstUser)) != null) return true;
    return false;
}

export function shouldUseKtasLevelUi(
    contentCode: string | null | undefined,
    row: TriagePatientRow,
    firstCorrect: string,
    firstUser: string,
): boolean {
    if (isRadiationDualStageContentCode(contentCode)) return false;
    if (isKtasContentCode(contentCode)) return true;
    return inferKtasFromRow(row, firstCorrect, firstUser);
}

export interface TriagePatientRow {
    patientName: string;
    /** boolean: true=남성, false=여성 (UI에서 변환) */
    gender: boolean;
    age: string;
    correctAnswer: string;
    userAnswer: string;
    firstCorrectAnswer?: string;
    firstUserAnswer?: string;
    secondCorrectAnswer?: string;
    secondUserAnswer?: string;
    correctRate?: number;
    /** START 방식 4가지 색상만 */
    correctRateByColor?: {
        red: number;
        yellow: number;
        green: number;
        black: number;
    };
    walkAbility?: string;
    responsiveness?: string;
    respiration?: string;
    pulse?: string;
    majorInjuries?: string[];
    chiefComplaint?: string;
    intervention?: string;
    comments?: string;
    ars?: string;
    pastHistory?: string;
    visitMotivation?: string;
    vitalSigns?: string;
    /** 모니터 UI 우선 표시 (없으면 vitalSigns·pulse·respiration에서 파싱) */
    bloodPressure?: string;
    temperature?: string;
    spo2?: string;
    bloodGlucose?: string;
    consciousnessLevel?: string;
    answerGcs?: string;
    answerMainSymptom?: string;
    answerPathway?: string;
    correctGcs?: string;
    correctMainSymptom?: string;
    correctKtasLevel?: string;
    correctPathway?: string;
}

export const getTriageStageAnswers = (row: TriagePatientRow) => {
	// 빈 문자열도 폴백하도록 || 사용
	const firstCorrect = row.firstCorrectAnswer && row.firstCorrectAnswer.trim() !== '' 
		? row.firstCorrectAnswer 
		: row.correctAnswer;
	const firstUser = row.firstUserAnswer && row.firstUserAnswer.trim() !== '' 
		? row.firstUserAnswer 
		: row.userAnswer;
	const secondCorrect = row.secondCorrectAnswer ?? '';
	const secondUser = row.secondUserAnswer ?? '';
	return { firstCorrect, firstUser, secondCorrect, secondUser };
};

export type TriageViewMode = 'card' | 'gallery' | 'winners';

/** 명시적 SALT·대량분류 시나리오 (`triage`) */
function isMassTriageContentCode(code: string | null | undefined): boolean {
    return String(code ?? '').trim().toLowerCase() === 'triage';
}

/**
 * 세션 단위 뷰 선택. `triage`는 항상 Mass, `ktas`/`radiation`은 고정,
 * `contentCode`가 비어 있을 때만 1행 데이터로 KTAS 추론.
 */
export function resolveTriageSessionVariant(
    contentCode: string | null | undefined,
    patients: TriagePatientRow[],
): 'mass' | 'ktas' | 'radiation' {
    if (isRadiationDualStageContentCode(contentCode)) return 'radiation';
    if (isKtasContentCode(contentCode)) return 'ktas';
    if (isMassTriageContentCode(contentCode)) return 'mass';
    const cc = String(contentCode ?? '').trim();
    if (cc !== '') return 'mass';
    const p0 = patients[0];
    if (p0) {
        const { firstCorrect, firstUser } = getTriageStageAnswers(p0);
        if (inferKtasFromRow(p0, String(firstCorrect), String(firstUser))) return 'ktas';
    }
    return 'mass';
}
